import { toSameOriginUrl } from 'src/core/classes/helpers/authorization'
import { getAppOwnerId } from 'src/core/classes/helpers/getAppId'
import { proxyIdentity } from 'src/core/classes/helpers/UrlHelper'
import { composeURL } from 'src/core/utils/functions'

// HP ID login v3. The API keeps the login credential in HttpOnly cookies on the host that set
// them. So the start, the provider callback and the exchange must reach that one host, and the
// site must be same-site with it, or SameSite=Lax keeps the cookie off the exchange request.

const CALLBACK_PATH = '/hpid-callback'
const RETURN_URL_KEY = 'hpid_return_url'
const PENDING_KEY = 'hpid_login_pending_until'
const PENDING_MS = 15 * 60 * 1000 // 10 min to the callback + 5 min to the exchange
const SIGN_OUT_TIMEOUT_MS = 5000
const MARKERS = ['auth', 'auth_error', 'auth_ref']
// Query parameters that sign a browser in. A return URL must never replay them after sign-in.
const SESSION_PARAMS = ['tokens', 'oneTime', 'oneTimeToken', ...MARKERS]
// auth_ref arrives in the URL, so anyone can write it. Show it only if it looks like a server id.
const REFERENCE_ID = /^[A-Za-z0-9:._-]{1,64}$/
const TARGETS = ['password', 'forgot-password', 'federation', 'create'] as const

// Our own code, not the API's: the callback came back, but no login was started in this browser
export const HPID_NOT_STARTED = 'not-started'

export type THpidTarget = (typeof TARGETS)[number]

export interface IHpidError {
  errorCode: string // a string on the wire: '4413', not 4413
  referenceId: string | null
}

export type THpidCallback = { flow: string } | { error: IHpidError }

export interface IHpidTokens {
  AccessToken: string
  RefreshToken: string
}

const providerUrl = (suffix = '') => {
  const ownerId = getAppOwnerId()
  if (!ownerId) {
    throw new Error('Application owner is not configured')
  }
  return proxyIdentity(`/mbe/api/v3/login/providers/${encodeURIComponent(ownerId)}${suffix}`)
}

// RootUrl may carry a path prefix, so match the end of the path, not all of it
const isCallbackPath = (pathname: string) =>
  pathname.replace(/\/+$/, '').endsWith(composeURL(CALLBACK_PATH))

export const toHpidTarget = (value: unknown): THpidTarget | undefined =>
  TARGETS.find((target) => target === value)

// signOutUrl must be a plain site-relative path: no query, hash, '\', control characters or dot
// segments. The API answers 4422 otherwise; we drop the query instead of failing sign-out.
export const toPlainPath = (url: string) =>
  toSameOriginUrl(url.split(/[?#]/)[0])?.pathname ?? composeURL('/')

// The page to return to after sign-in. It comes from #redirectPage=, which anyone can put in a
// link, and no server checks it (redirectUrl sent to the API is the constant CALLBACK_PATH).
// So it is checked when stored and again when read. Only a same-origin path survives; the
// callback and login pages fall back to '/'.
const toReturnPath = (value: unknown): string => {
  const url = toSameOriginUrl(value)
  if (!url || isCallbackPath(url.pathname) || url.pathname === composeURL('/login')) {
    return composeURL('/')
  }
  // Touch the query only when it carries a sign-in parameter: deleting from searchParams
  // re-encodes the whole query ('ids=1,2' becomes 'ids=1%2C2')
  if (SESSION_PARAMS.some((key) => url.searchParams.has(key))) {
    SESSION_PARAMS.forEach((key) => url.searchParams.delete(key))
  }
  return `${url.pathname}${url.search}${url.hash}`
}

// Returns the start URL; the caller must open it with a real navigation (location.assign or
// replace). The answer is a 302 that sets an HttpOnly cookie, which an XHR cannot do.
export const prepareHpidLogin = (returnUrl: string, target?: THpidTarget): string => {
  const startUrl = providerUrl() // throws before anything is stored if the owner is missing

  // The return page may carry a query string; v3 forbids one in redirectUrl, so it waits here
  sessionStorage.setItem(RETURN_URL_KEY, toReturnPath(returnUrl))
  localStorage.setItem(PENDING_KEY, String(Date.now() + PENDING_MS))

  const query = new URLSearchParams({ redirectUrl: composeURL(CALLBACK_PATH) })
  if (target) {
    query.set('target', target)
  }
  return `${startUrl}?${query}`
}

// Reads the v3 markers and removes them from the URL before any await. A second initAuth run
// must not redeem the same single-use flow, or the user sees 4414 after a successful login.
export const takeHpidCallback = (): THpidCallback | null => {
  if (!isCallbackPath(window.location.pathname)) {
    return null
  }
  const url = new URL(window.location.href)
  const flow = url.searchParams.get('auth')
  const errorCode = url.searchParams.get('auth_error')
  const referenceId = url.searchParams.get('auth_ref')
  if (!flow && !errorCode) {
    return null
  }
  MARKERS.forEach((key) => url.searchParams.delete(key))
  window.history.replaceState(window.history.state, '', url.toString())

  return errorCode ? { error: { errorCode, referenceId } } : { flow: flow ?? '' }
}

export const takeHpidReturnUrl = () => {
  const returnUrl = sessionStorage.getItem(RETURN_URL_KEY)
  sessionStorage.removeItem(RETURN_URL_KEY)
  // Checked again: any script on the origin can write sessionStorage
  return toReturnPath(returnUrl)
}

// A backstop, not the defence: the defence is __Host- cookies on the API. It only proves that a
// login was started in this browser in the last 15 minutes, not that this flow is that login.
// localStorage, not sessionStorage: HP ID may finish account creation in a new tab. Consumed on
// the first redemption attempt, so a second parallel login has to retry.
export const hasPendingHpidLogin = () => Number(localStorage.getItem(PENDING_KEY)) > Date.now()

export const clearPendingHpidLogin = () => localStorage.removeItem(PENDING_KEY)

// The error body is { errorCode, referenceId }; either key may be missing, or the body is empty
const toHpidError = (body: unknown): IHpidError => {
  const { errorCode, referenceId } = (
    body && typeof body === 'object' ? body : {}
  ) as Partial<IHpidError>
  return {
    errorCode: typeof errorCode === 'string' ? errorCode : '',
    referenceId: typeof referenceId === 'string' ? referenceId : null,
  }
}

// Plain fetch, not Rest: no Authorization header, no refresh-and-retry on 401, no 429 retry,
// no request queue. The flow is single use (5 minutes); any automatic retry turns a success
// into 4414. A network error is reported, never retried. No timeout on purpose: aborting would
// leave it unknown whether the server already redeemed the flow.
export const exchangeHpidFlow = async (flow: string): Promise<IHpidTokens> => {
  let response: Response
  try {
    response = await fetch(proxyIdentity('/mbe/api/v3/access/token'), {
      method: 'POST',
      credentials: 'include', // the session cookie is the credential
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ flow }),
    })
  } catch {
    throw toHpidError(null)
  }
  // An error body may be empty (not even '{}'): response.json() would throw on it
  const body = await response.json().catch(() => null)
  if (!response.ok) {
    throw toHpidError(body)
  }
  if (!body?.AccessToken || !body?.RefreshToken) {
    throw toHpidError(null)
  }
  return body as IHpidTokens
}

// Plain fetch with its own timeout: logout must not wait behind HttpRequest's 401 refresh
// blocker. AbortController + setTimeout rather than AbortSignal.timeout, which jsdom 20
// (the Jest environment) and Safari < 16 do not have. Any failure means "no HP sign-out".
export const fetchHpidSignOutUrl = async (
  accessToken: string,
  returnUrl = '/'
): Promise<string | null> => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), SIGN_OUT_TIMEOUT_MS)
  try {
    const response = await fetch(providerUrl('/signout-url'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ signOutUrl: toPlainPath(returnUrl) }),
      signal: controller.signal,
    })
    const body = response.ok ? await response.json().catch(() => null) : null
    return typeof body?.url === 'string' ? body.url : null
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}

// A Map, not an object: auth_error comes from the URL, and ERROR_TEXT['constructor'] would
// resolve to Object's constructor
const ERROR_TEXT = new Map<string, string>([
  ['4410', 'This sign-in took too long. Please try again.'],
  [
    '4411',
    'We could not confirm this sign-in in your browser. Check that cookies are enabled and try again.',
  ],
  ['4412', 'We could not confirm this sign-in. Please try again.'],
  ['4413', 'Your sign-in session was not found. Please sign in again.'],
  ['4414', 'This sign-in link has already been used. Please sign in again.'],
  ['4415', 'Sign-in was cancelled.'],
  ['4416', 'Sign-in did not complete. Please try again.'],
  ['4417', 'Sign-in is temporarily unavailable. Please try again in a few minutes.'],
  [HPID_NOT_STARTED, 'This sign-in was not started in this browser. Please sign in again.'],
])

// The API returns no text; referenceId is the only thing support can search the server log for
export const getHpidErrorMessage = ({ errorCode, referenceId }: IHpidError) => {
  const text = ERROR_TEXT.get(errorCode) ?? 'Sign-in did not complete. Please try again.'
  return referenceId && REFERENCE_ID.test(referenceId) ? `${text} Reference: ${referenceId}` : text
}
